import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { WorkspaceTab } from '@/stores/app'

interface UseTabOverflowArgs {
  tabs: WorkspaceTab[]
  activeId: string | null
  containerRef: React.RefObject<HTMLElement | null>
  /** px reserved for the "+N" button when overflow exists (Pass B only). */
  overflowButtonWidth?: number
}

interface UseTabOverflowResult {
  visibleTabs: WorkspaceTab[]
  overflowTabs: WorkspaceTab[]
  /**
   * Returns a STABLE ref callback for the given id. The same callback
   * identity is returned on every render, so React doesn't fire
   * cleanup-then-new-attach on every parent re-render. Tabs in the
   * measurement row attach this ref to register their element for width
   * measurement.
   */
  getMeasureRef: (id: string) => (el: HTMLElement | null) => void
}

/**
 * Render-and-measure tab overflow.
 *
 * Strategy
 *   - The host renders a HIDDEN measurement row (`visibility: hidden`,
 *     `position: absolute`, `aria-hidden`) containing every tab's plain
 *     title, plus a normal visible row showing the resolved split.
 *   - Each measurement-row tab element registers itself via
 *     `getMeasureRef(id)`, which returns a stable ref callback that
 *     attaches a per-element ResizeObserver. Widths land in a
 *     `useRef<Map>` (NOT React state) to avoid re-render churn.
 *   - A container ResizeObserver watches the available width.
 *   - On `tabs` change, container width change, or per-tab width change
 *     (via `widthsVersion` counter bumped from RO callbacks), the
 *     visible/overflow split is recomputed.
 *
 * Algorithm
 *   Pass A — no reserve: accumulate widths in order; if total fits, all
 *   visible, no `+N` button.
 *   Pass B — with reserve (only if Pass A overflowed): reserve
 *   `overflowButtonWidth` from the budget; tabs that don't fit go to
 *   overflow.
 *
 * Active-tab visibility invariant
 *   If `activeId` falls in `overflowTabs`, the LAST entry of `visibleTabs`
 *   is displaced into overflow and `activeId` is appended at the END of
 *   `visibleTabs`. Original tab order is preserved for everything else.
 *
 * Initial mount: a single-frame "all visible" fallback is possible while
 * widths haven't reported yet. `useLayoutEffect` reads container width
 * synchronously after mount to minimize the window.
 */
export function useTabOverflow({
  tabs,
  activeId,
  containerRef,
  overflowButtonWidth = 48,
}: UseTabOverflowArgs): UseTabOverflowResult {
  const widthsRef = useRef<Map<string, number>>(new Map())
  const observersRef = useRef<Map<string, ResizeObserver>>(new Map())
  const refCallbacksRef = useRef<Map<string, (el: HTMLElement | null) => void>>(
    new Map()
  )
  const [containerWidth, setContainerWidth] = useState(0)
  const [widthsVersion, setWidthsVersion] = useState(0)

  // Per-id stable ref callback. The same function identity is returned
  // on every render for a given id, so React invokes the callback only
  // on mount (with the element) and unmount (with null) — never on every
  // re-render. This is the load-bearing fix that prevents the infinite
  // render loop an inline ref callback would cause.
  const getMeasureRef = useCallback(
    (id: string): ((el: HTMLElement | null) => void) => {
      const cached = refCallbacksRef.current.get(id)
      if (cached) return cached

      const cb = (el: HTMLElement | null) => {
        const observers = observersRef.current
        if (!el) {
          // React 19 StrictMode fires (mount, unmount, mount) for every
          // ref. If we delete the cached callback here, the next render
          // creates a NEW callback identity, which causes React to fire
          // cleanup+attach AGAIN (the OLD callback's null then deletes
          // the JUST-cached new callback) → infinite loop. The cache
          // entry must persist for the lifetime of the id. We also
          // don't bump `widthsVersion` here because the parent's next
          // render (driven by tabs change) already triggers the useMemo
          // recompute; bumping here just causes an extra render.
          const existing = observers.get(id)
          if (existing) {
            existing.disconnect()
            observers.delete(id)
          }
          widthsRef.current.delete(id)
          return
        }

        const measured = el.getBoundingClientRect().width
        const prev = widthsRef.current.get(id)
        if (prev !== measured) {
          widthsRef.current.set(id, measured)
          setWidthsVersion((v) => v + 1)
        }

        if (observers.has(id)) return
        const ro = new ResizeObserver((entries) => {
          const entry = entries[0]
          if (!entry) return
          const w = entry.contentRect.width
          if (widthsRef.current.get(id) === w) return
          widthsRef.current.set(id, w)
          setWidthsVersion((v) => v + 1)
        })
        ro.observe(el)
        observers.set(id, ro)
      }
      refCallbacksRef.current.set(id, cb)
      return cb
    },
    []
  )

  // Container width observer (synchronous initial read + observer).
  // Both reads use the content-box width — children render inside the
  // padding zone, so the budget must reflect content-box, not padding-box.
  // Mixing clientWidth (padding-box) with contentRect.width (content-box)
  // produced a one-frame flicker on mount equal to the container's padding.
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const style = getComputedStyle(el)
    const pl = parseFloat(style.paddingLeft) || 0
    const pr = parseFloat(style.paddingRight) || 0
    setContainerWidth(el.clientWidth - pl - pr)
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      setContainerWidth(entry.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [containerRef])

  // Disconnect all observers on unmount.
  useEffect(() => {
    const observers = observersRef.current
    return () => {
      for (const ro of observers.values()) ro.disconnect()
      observers.clear()
    }
  }, [])

  return useMemo(() => {
    if (tabs.length === 0)
      return { visibleTabs: [], overflowTabs: [], getMeasureRef }
    if (containerWidth <= 0) {
      return { visibleTabs: tabs, overflowTabs: [], getMeasureRef }
    }

    const widths = widthsRef.current
    // Bail-out: any tab missing a measurement → can't decide yet. Show all.
    for (const tab of tabs) {
      if (!widths.has(tab.id)) {
        return { visibleTabs: tabs, overflowTabs: [], getMeasureRef }
      }
    }

    const totalWidth = tabs.reduce((sum, t) => sum + (widths.get(t.id) ?? 0), 0)

    // Pass A — no reserve.
    if (totalWidth <= containerWidth) {
      return { visibleTabs: tabs, overflowTabs: [], getMeasureRef }
    }

    // Pass B — with reserve for the +N button.
    const budget = containerWidth - overflowButtonWidth
    const visible: WorkspaceTab[] = []
    const overflow: WorkspaceTab[] = []
    let used = 0
    for (const tab of tabs) {
      const w = widths.get(tab.id) ?? 0
      if (used + w <= budget) {
        visible.push(tab)
        used += w
      } else {
        overflow.push(tab)
      }
    }

    // Active-tab visibility invariant: if active is in overflow, pop
    // rightmost visible tabs until active fits within budget, then append
    // active. Force-include active even when visible is empty — one
    // overflowing tab is better UX than an empty strip with no active
    // indicator.
    if (activeId && overflow.some((t) => t.id === activeId)) {
      const activeIdx = overflow.findIndex((t) => t.id === activeId)
      const activeTab = overflow[activeIdx]
      if (activeTab) {
        const activeW = widths.get(activeId) ?? 0
        const extraOverflow: WorkspaceTab[] = []

        while (visible.length > 0 && used + activeW > budget) {
          const popped = visible.pop()!
          used -= widths.get(popped.id) ?? 0
          extraOverflow.unshift(popped)
        }

        // Remove active from overflow, add it to the end of visible.
        overflow.splice(activeIdx, 1)
        visible.push(activeTab)
        used += activeW

        // Re-insert displaced tabs into overflow at their source position.
        for (const d of extraOverflow) {
          const displacedIdx = tabs.findIndex((t) => t.id === d.id)
          const insertAt = overflow.findIndex((t) => {
            const i = tabs.findIndex((tt) => tt.id === t.id)
            return i > displacedIdx
          })
          if (insertAt === -1) {
            overflow.push(d)
          } else {
            overflow.splice(insertAt, 0, d)
          }
        }
      }
    }

    // Packing pass — re-try every overflowed tab against the leftover
    // budget. The Pass B loop checks each tab against the running `used`
    // total, but the active-invariant block can free up budget by
    // popping wide tabs to make room for a narrow active tab. After
    // that, some short overflowed tabs may now fit. Without this pass,
    // those short tabs stay in overflow even though they'd display
    // cleanly — visible to the user as "the +N dropdown has tabs that
    // could obviously fit." Iterates in source order; tabs that fit
    // append to the end of `visible` (which preserves source order
    // because all overflow entries are later than all visible entries
    // in `tabs`).
    {
      const stillOverflow: WorkspaceTab[] = []
      for (const tab of overflow) {
        const w = widths.get(tab.id) ?? 0
        if (used + w <= budget) {
          visible.push(tab)
          used += w
        } else {
          stillOverflow.push(tab)
        }
      }
      overflow.length = 0
      overflow.push(...stillOverflow)
    }

    return { visibleTabs: visible, overflowTabs: overflow, getMeasureRef }
    // widthsVersion is intentional — it's a re-render signal bumped by the
    // per-tab ResizeObserver callbacks when widths change. The memo body
    // reads widthsRef.current.get(id), not widthsVersion directly, so
    // the hook lint rule doesn't see the data dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tabs,
    containerWidth,
    widthsVersion,
    activeId,
    overflowButtonWidth,
    getMeasureRef,
  ])
}
